import { getAccessToken, waitForAccessToken } from "./authToken";

/**
 * Shared API stage root for lead summary and leads list (GET `{base}/lead-summary/...`, POST `{base}/leads`).
 * Prefer `VITE_LEAD_SERVICES_BASE`; fall back to `VITE_LEAD_SUMMARY_API_BASE` for older configs.
 */
export function getLeadSummaryApiBase() {
    const unified = String(import.meta.env.VITE_LEAD_SERVICES_BASE || "")
        .trim()
        .replace(/\/$/, "");
    if (unified) {
        return unified;
    }
    return String(import.meta.env.VITE_LEAD_SUMMARY_API_BASE || "")
        .trim()
        .replace(/\/$/, "");
}

/**
 * Use API-backed lead detail when summary base URL is configured.
 */
export function shouldUseLeadSummaryRemote() {
    return !!getLeadSummaryApiBase();
}

/**
 * GET {base}/lead-summary/{cin} with Okta ID token (Bearer).
 * Response includes `lead_summary` plus `member_details` (demographics, pof, badges).
 */
export async function fetchLeadSummaryByCin(cin) {
    const base = getLeadSummaryApiBase();
    if (!base) {
        throw new Error(
            "Lead summary API is not configured (set VITE_LEAD_SERVICES_BASE or VITE_LEAD_SUMMARY_API_BASE)."
        );
    }
    const id = String(cin || "").trim();
    if (!id) {
        throw new Error("CIN is required.");
    }

    await waitForAccessToken(5000);
    const token = getAccessToken();
    if (!token) {
        throw new Error("You must be signed in to view lead summaries.");
    }

    const url = `${base}/lead-summary/${encodeURIComponent(id)}`;
    const res = await fetch(url, {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`
        }
    });

    if (!res.ok) {
        let detail = "";
        try {
            const errBody = await res.json();
            detail = errBody.message || errBody.error || errBody.detail || "";
        } catch {
            try {
                detail = await res.text();
            } catch {
                detail = "";
            }
        }
        const msg = detail ? `${detail}` : `Request failed (${res.status})`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }

    return res.json();
}

/**
 * POST {base}/lead-summary/{cin}/generate to refresh the summary.
 * Response: { cin_no, summary_date, status }
 */
export async function generateLeadSummaryByCin(cin) {
    const base = getLeadSummaryApiBase();
    if (!base) {
        throw new Error(
            "Lead summary API is not configured (set VITE_LEAD_SERVICES_BASE or VITE_LEAD_SUMMARY_API_BASE)."
        );
    }
    const id = String(cin || "").trim();
    if (!id) {
        throw new Error("CIN is required.");
    }

    await waitForAccessToken(5000);
    const token = getAccessToken();
    if (!token) {
        throw new Error("You must be signed in to refresh lead summaries.");
    }

    const url = `${base}/lead-summary/${encodeURIComponent(id)}/generate`;
    const res = await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`
        }
    });

    if (!res.ok) {
        let detail = "";
        try {
            const errBody = await res.json();
            detail = errBody.message || errBody.error || errBody.detail || "";
        } catch {
            try {
                detail = await res.text();
            } catch {
                detail = "";
            }
        }
        const msg = detail ? `${detail}` : `Request failed (${res.status})`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }

    return res.json();
}
